import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

interface HelpDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function HelpDialog({
    open,
    onOpenChange,
}: HelpDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-semibold">
                        Manage Partners
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 text-sm text-gray-700 leading-relaxed">

                    <h2 className="text-lg font-semibold text-black">
                        Partner Management
                    </h2>

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Creating Partners
                        </h3>

                        <p>
                            Users can navigate to Manage Partners through the
                            application hexagons and create new partner records.
                        </p>

                        <ol className="space-y-2 ml-1">
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 1 –
                                </span>
                                <span>
                                    Click the <span className="font-medium">Add Partner</span> button.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 2 –
                                </span>
                                <span>
                                    Select <span className="font-medium">Partner</span> from the Partner Category dropdown.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 3 –
                                </span>
                                <span>
                                    Enter the Partner Name and Description.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 4 –
                                </span>
                                <span>
                                    Select a Partner Type from the dropdown.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 5 –
                                </span>
                                <span>
                                    Enter the total number of partners to create.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 6 –
                                </span>
                                <span>
                                    Save the record. The system will create the specified number of partners and display them sequentially.
                                </span>
                            </li>
                        </ol>

                        <div>
                            <p className="font-semibold text-black">
                                Additional Information
                            </p>

                            <ul className="list-disc ml-5 mt-1 space-y-1">
                                <li>Partner Types are populated from Code Manager.</li>
                                <li>Partners are displayed with sequence numbers.</li>
                            </ul>
                        </div>
                    </section>

                    {/* CREATING AGENCIES */}

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Creating Agencies
                        </h3>

                        <p>
                            Agencies can be created using the same process as partner creation.
                        </p>

                        <ol className="space-y-2 ml-1">
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 1 –
                                </span>
                                <span>
                                    Click <span className="font-medium">Add Partner</span>.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 2 –
                                </span>
                                <span>
                                    Select <span className="font-medium">Agency</span> from Partner Category.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 3 –
                                </span>
                                <span>
                                    Enter Agency Name and Description.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 4 –
                                </span>
                                <span>
                                    Select Partner Type and total count.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 5 –
                                </span>
                                <span>
                                    Save to create the agencies.
                                </span>
                            </li>
                        </ol>
                    </section>

                    {/* CREATING DEPARTMENTS */}

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Creating Departments
                        </h3>

                        <ol className="space-y-2 ml-1">
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 1 –
                                </span>
                                <span>
                                    Click <span className="font-medium">Add Partner</span>.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 2 –
                                </span>
                                <span>
                                    Select <span className="font-medium">Department</span> from Partner Category.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 3 –
                                </span>
                                <span>
                                    Enter Department Name and Description.
                                </span>
                            </li>
                        </ol>
                    </section>

                    {/* EXCHANGE OFFERS */}

                    <h2 className="text-lg font-semibold text-black">
                        Exchange Offers
                    </h2>

                    <section className="space-y-3">
                        <p>
                            Exchange Offers allow users to manage offers associated
                            with partners and agencies.
                        </p>

                        <h3 className="text-base font-bold text-black">
                            Add Exchange Offers
                        </h3>

                        <ol className="space-y-2 ml-1">
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 1 –
                                </span>
                                <span>
                                    Click the <span className="font-medium">Add Offer</span> button.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 2 –
                                </span>
                                <span>
                                    Select one or multiple partners from the Partner dropdown.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 3 –
                                </span>
                                <span>
                                    Enter the Offer Name.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 4 –
                                </span>
                                <span>
                                    Enter offer details and save.
                                </span>
                            </li>
                        </ol>
                    </section>

                    {/* INDIVIDUAL OFFERS */}

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Individual Offers
                        </h3>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Click Individual Offers.</li>
                            <li>Enter the Offer Name.</li>
                            <li>Specify the Offer Validation Date.</li>
                            <li>Save the offer.</li>
                        </ul>
                    </section>

                    {/* RULE VALUE SETUP */}

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Rule Value Setup
                        </h3>

                        <p>
                            Approved rules from Rules Manager are available for
                            configuration within offers.
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Assign rule values.</li>
                            <li>Modify existing rule configurations.</li>
                            <li>Control offer eligibility and applicability.</li>
                        </ul>
                    </section>

                    {/* DEFAULT OFFERS */}

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Default Offers (Form Setup)
                        </h3>

                        <p>
                            The View/Setup Offers link redirects users to the
                            Forms Engine.
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Attach a form to an offer.</li>
                            <li>Only one form can be associated with an offer.</li>
                        </ul>
                    </section>

                    {/* ADDITIONAL OFFERS */}

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Additional Offer Setup
                        </h3>

                        <p>
                            Additional offers can be configured within an
                            existing primary offer.
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Displays Raw Rules and Modified Rules.</li>
                            <li>Displays configured rule values.</li>
                            <li>Setup/View Additional Offers redirects to Forms Manager.</li>
                        </ul>
                    </section>

                    {/* EXAMPLE */}

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Common Example
                        </h3>

                        <div className="bg-gray-100 p-3 rounded">
                            <p>
                                <strong>Partner:</strong> HDFC Bank
                            </p>

                            <p>
                                <strong>Associated Partner:</strong> Amazon
                            </p>

                            <p>
                                <strong>Offer:</strong> Get 10% instant discount
                                using HDFC credit cards.
                            </p>
                        </div>
                    </section>

                    {/* EDIT DELETE */}

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Edit and Delete Partner
                        </h3>

                        <p>
                            Partners, Agencies, and Departments can be modified
                            using actions available in the Action column.
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>
                                Click the Edit icon to update partner details.
                            </li>
                            <li>
                                Click the Delete icon and confirm deletion to
                                remove the selected record.
                            </li>
                        </ul>
                    </section>

                </div>
            </DialogContent>
        </Dialog>
    );
}
